import { HugeiconsIcon } from "@hugeicons/react";
import {
  Alert01Icon,
  Analytics01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  ArrowUpRight01Icon,
  AtIcon,
  BankIcon,
  Building03Icon,
  Calendar03Icon,
  Call02Icon,
  Camera01Icon,
  Cancel01Icon,
  CheckmarkCircle01Icon,
  CheckmarkSquare02Icon,
  CircleIcon,
  Clock01Icon,
  Copy01Icon,
  Download01Icon,
  Facebook01Icon,
  Globe02Icon,
  Home01Icon,
  InformationCircleIcon,
  InstagramIcon,
  LanguageCircleIcon,
  LayoutGridIcon,
  Linkedin01Icon,
  Link01Icon,
  ListViewIcon,
  Location01Icon,
  LockIcon,
  Login01Icon,
  Mail01Icon,
  Menu01Icon,
  Message01Icon,
  Moon02Icon,
  Plant01Icon,
  PlusSignIcon,
  QrCodeIcon,
  RobotIcon,
  RulerIcon,
  Search01Icon,
  SentIcon,
  Settings01Icon,
  Shield01Icon,
  ShoppingCart01Icon,
  SnapchatIcon,
  StarHalfIcon,
  StarIcon,
  StarOffIcon,
  Sun03Icon,
  Target01Icon,
  TiktokIcon,
  TwitterIcon,
  Upload01Icon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  Wallet01Icon,
  YoutubeIcon,
} from "@hugeicons-pro/core-solid-standard";
import type { CSSProperties } from "react";

type IconProps = {
  size?: number | string;
  color?: string;
  fill?: string;
  stroke?: number | string;
  withOutline?: boolean;
  className?: string;
  style?: CSSProperties;
};

const fallbackIcon = Search01Icon;

function toNumber(value: number | string | undefined, fallback: number) {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

/** Inherits tag/button text color when used inside Mantine Badge, Button, etc. */
function makeIcon(icon: typeof Search01Icon, displayName: string) {
  const resolved = icon ?? fallbackIcon;
  function IconComponent({
    size = 20,
    color,
    fill,
    className,
    style,
  }: IconProps) {
    return (
      <HugeiconsIcon
        icon={resolved}
        size={toNumber(size, 20)}
        color={color ?? fill ?? "currentColor"}
        className={className}
        style={style}
      />
    );
  }
  IconComponent.displayName = displayName;
  return IconComponent;
}

export const IconActivityHeartbeat = makeIcon(Search01Icon, "IconActivityHeartbeat");
export const IconAffiliate = makeIcon(Search01Icon, "IconAffiliate");
export const IconAlertCircle = makeIcon(Alert01Icon, "IconAlertCircle");
export const IconApps = makeIcon(Search01Icon, "IconApps");
export const IconArrowUp = makeIcon(ArrowUp01Icon, "IconArrowUp");
export const IconAt = makeIcon(AtIcon, "IconAt");
export const IconBasketOff = makeIcon(Search01Icon, "IconBasketOff");
export const IconBathroomFilled = makeIcon(Search01Icon, "IconBathroomFilled");
export const IconBedFilled = makeIcon(Search01Icon, "IconBedFilled");
export const IconBrandInstagram = makeIcon(InstagramIcon, "IconBrandInstagram");
export const IconBrandLinkedin = makeIcon(Linkedin01Icon, "IconBrandLinkedin");
export const IconBrandSnapchat = makeIcon(SnapchatIcon, "IconBrandSnapchat");
export const IconBrandTiktok = makeIcon(TiktokIcon, "IconBrandTiktok");
export const IconBrandTwitter = makeIcon(TwitterIcon, "IconBrandTwitter");
export const IconBrandX = makeIcon(TwitterIcon, "IconBrandX");
export const IconBrandYoutube = makeIcon(YoutubeIcon, "IconBrandYoutube");
export const IconBuildingBank = makeIcon(BankIcon, "IconBuildingBank");
export const IconBuildings = makeIcon(Building03Icon, "IconBuildings");
export const IconCalendar = makeIcon(Calendar03Icon, "IconCalendar");
export const IconCamera = makeIcon(Camera01Icon, "IconCamera");
export const IconCheck = makeIcon(CheckmarkSquare02Icon, "IconCheck");
export const IconCheckbox = makeIcon(CheckmarkSquare02Icon, "IconCheckbox");
export const IconChevronDown = makeIcon(ArrowDown01Icon, "IconChevronDown");
export const IconCircleCheck = makeIcon(CheckmarkCircle01Icon, "IconCircleCheck");
export const IconCircleFilled = makeIcon(CircleIcon, "IconCircleFilled");
export const IconClock = makeIcon(Clock01Icon, "IconClock");
export const IconCopy = makeIcon(Copy01Icon, "IconCopy");
export const IconDownload = makeIcon(Download01Icon, "IconDownload");
export const IconEdit = makeIcon(Search01Icon, "IconEdit");
export const IconExternalLink = makeIcon(Link01Icon, "IconExternalLink");
export const IconFileUpload = makeIcon(Upload01Icon, "IconFileUpload");
export const IconGlobe = makeIcon(Globe02Icon, "IconGlobe");
export const IconHeart = makeIcon(Search01Icon, "IconHeart");
export const IconHeartFilled = makeIcon(Search01Icon, "IconHeartFilled");
export const IconHouseFilled = makeIcon(Home01Icon, "IconHouseFilled");
export const IconLanguage = makeIcon(LanguageCircleIcon, "IconLanguage");
export const IconLayoutGrid = makeIcon(LayoutGridIcon, "IconLayoutGrid");
export const IconListView = makeIcon(ListViewIcon, "IconListView");
export const IconLock = makeIcon(LockIcon, "IconLock");
export const IconLockFilled = makeIcon(LockIcon, "IconLockFilled");
export const IconLogout = makeIcon(Login01Icon, "IconLogout");
export const IconMail = makeIcon(Mail01Icon, "IconMail");
export const IconMailFilled = makeIcon(Mail01Icon, "IconMailFilled");
export const IconMapPin = makeIcon(Location01Icon, "IconMapPin");
export const IconMenu2 = makeIcon(Menu01Icon, "IconMenu2");
export const IconMessage = makeIcon(Message01Icon, "IconMessage");
export const IconMessageCircle = makeIcon(Message01Icon, "IconMessageCircle");
export const IconMessageDots = makeIcon(Message01Icon, "IconMessageDots");
export const IconMoon = makeIcon(Moon02Icon, "IconMoon");
export const IconPhone = makeIcon(Call02Icon, "IconPhone");
export const IconPhoneFilled = makeIcon(Call02Icon, "IconPhoneFilled");
export const IconPlant = makeIcon(Plant01Icon, "IconPlant");
export const IconPlugConnected = makeIcon(Search01Icon, "IconPlugConnected");
export const IconPlugConnectedX = makeIcon(Search01Icon, "IconPlugConnectedX");
export const IconPlus = makeIcon(PlusSignIcon, "IconPlus");
export const IconQRCode = makeIcon(QrCodeIcon, "IconQRCode");
export const IconRobot = makeIcon(RobotIcon, "IconRobot");
export const IconRulerMeasureOutlined = makeIcon(RulerIcon, "IconRulerMeasureOutlined");
export const IconSearch = makeIcon(Search01Icon, "IconSearch");
export const IconSend = makeIcon(SentIcon, "IconSend");
export const IconSettings = makeIcon(Settings01Icon, "IconSettings");
export const IconShield = makeIcon(Shield01Icon, "IconShield");
export const IconShoppingCart = makeIcon(ShoppingCart01Icon, "IconShoppingCart");
export const IconStar = makeIcon(StarIcon, "IconStar");
export const IconStarFilled = makeIcon(StarIcon, "IconStarFilled");
export const IconStarHalf = makeIcon(StarHalfIcon, "IconStarHalf");
export const IconStarOff = makeIcon(StarOffIcon, "IconStarOff");
export const IconSun = makeIcon(Sun03Icon, "IconSun");
export const IconTarget = makeIcon(Target01Icon, "IconTarget");
export const IconTrendingUp = makeIcon(ArrowUpRight01Icon, "IconTrendingUp");
export const IconUser = makeIcon(UserIcon, "IconUser");
export const IconUserCircle = makeIcon(UserCircleIcon, "IconUserCircle");
export const IconUserFilled = makeIcon(UserIcon, "IconUserFilled");
export const IconUserOutlined = makeIcon(UserCircleIcon, "IconUserOutlined");
export const IconUsers = makeIcon(UserGroupIcon, "IconUsers");
export const IconUsersGroup = makeIcon(UserGroupIcon, "IconUsersGroup");
export const IconWallet = makeIcon(Wallet01Icon, "IconWallet");
export const IconWorld = makeIcon(Globe02Icon, "IconWorld");
export const IconWorldWww = makeIcon(Globe02Icon, "IconWorldWww");
export const IconX = makeIcon(Cancel01Icon, "IconX");
export const Iconrefresh = makeIcon(Search01Icon, "Iconrefresh");

export const IconBrandFacebook = makeIcon(Facebook01Icon, "IconBrandFacebook");
export const IconInfoCircle = makeIcon(InformationCircleIcon, "IconInfoCircle");
export const IconChartHistogram = makeIcon(Analytics01Icon, "IconChartHistogram");
